import { useState } from 'react'
import {
  Text,
  CardContainer,
  Icon,
  constants,
  Grid,
  Button,
  hooks,
  NumberInputField,
  DropdownField,
} from '@adc/ui-adc'
import classname from 'classnames/bind'
import {
  AddressBoxProps,
  PropertyBoxProps,
  PropertyFieldsProps,
  fieldsInPropertyDetails,
  ViewFieldProps,
} from './typesDefinition'
import { getPropertyField } from '../../utils'
import styles from './styles.scss'

const cx = classname.bind(styles)

const { useIsMobileHook } = hooks

export const AddressBox = ({ address, showIsConfirmed }: AddressBoxProps) => {
  return (
    <div className={cx('address-container')}>
      <CardContainer
        padding='small'
        borderRadius='small'
        borderRadiusCorners='all'
        borderColor={constants.ColorNames.GrayLight}
        backgroundColor={constants.ColorNames.OffWhite}
      >
        <Text weight='medium' elmId='address_box_label'>
          {address}
        </Text>
      </CardContainer>
      {showIsConfirmed && (
        <CardContainer
          padding='small'
          borderRadius='small'
          borderRadiusCorners='all'
          backgroundColor={constants.ColorNames.SuccessExtraLight}
        >
          <Grid>
            <Grid.Row>
              <Grid.Column
                computer={2}
                tablet={3}
                mobile={4}
                verticalAlign='middle'
                className={cx('property-confirmed-icon-container')}
              >
                <Icon
                  name='check circle'
                  color={constants.ColorNames.SuccessActiveMedium}
                  size='big'
                  className={cx('property-confirmed-icon')}
                  elmId='property_match_icon'
                />
              </Grid.Column>
              <Grid.Column
                computer={14}
                tablet={13}
                mobile={12}
                verticalAlign='middle'
              >
                <Text
                  weight='heavy'
                  color={constants.ColorNames.AdcDarkGreen}
                  elmId='property_match_title'
                >
                  Property Match Confirmed
                </Text>
                <Text
                  color={constants.ColorNames.Success}
                  size='tiny'
                  elmId='property_match_subtitle'
                >
                  We found your property in our database with property details
                  and market data.
                </Text>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </CardContainer>
      )}
    </div>
  )
}
const ViewField = ({ label, value }: ViewFieldProps) => {
  const isMobile = useIsMobileHook()
  return (
    <Grid>
      <Grid.Row>
        <Grid.Column width={8}>
          <Text.Body
            elmId={`${label}_label`}
            weight='medium'
            color={constants.ColorNames.GrayMedium}
          >
            {label}
          </Text.Body>
        </Grid.Column>
        <Grid.Column
          width={8}
          className={isMobile ? cx('align-text-right') : ''}
        >
          <Text.Body
            elmId={`${label}_value`}
            weight='heavy'
            color={constants.ColorNames.Gray}
          >
            {value}
          </Text.Body>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

const PropertyFields = ({ property }: PropertyFieldsProps) => {
  return (
    <Grid>
      <Grid.Row>
        {fieldsInPropertyDetails.map((field, index) => (
          <Grid.Column
            computer={8}
            tablet={8}
            mobile={16}
            className={cx('fields-row')}
            key={index}
          >
            <ViewField
              label={field.label}
              value={getPropertyField(
                property,
                field.path as keyof PropertyBoxProps['property']
              )}
            />
          </Grid.Column>
        ))}
      </Grid.Row>
    </Grid>
  )
}

const PropertyForm = () => {
  return (
    <Grid>
      <Grid.Row>
        <Grid.Column
          computer={8}
          tablet={8}
          mobile={16}
          className={cx('fields-row')}
        >
          <NumberInputField
            label='Beds'
            elmId='total_bedrooms'
            name='total_bedrooms'
            maskOptions={{ allowLeadingZeroes: true }}
            showIncrementButtons
          />
        </Grid.Column>
        <Grid.Column
          computer={8}
          tablet={8}
          mobile={16}
          className={cx('fields-row')}
        >
          <NumberInputField
            label='Year Built'
            elmId='year_built'
            name='year_built'
            maskOptions={{
              allowDecimal: false,
              allowLeadingZeroes: false,
              integerLimit: 4,
            }}
          />
        </Grid.Column>
        <Grid.Column
          computer={8}
          tablet={8}
          mobile={16}
          className={cx('fields-row')}
        >
          <NumberInputField
            label='Baths'
            elmId='total_bathrooms'
            name='total_bathrooms'
            maskOptions={{ allowLeadingZeroes: true }}
            showIncrementButtons
          />
        </Grid.Column>
        <Grid.Column
          computer={8}
          tablet={8}
          mobile={16}
          className={cx('fields-row')}
        >
          <NumberInputField
            label='Lot Size (Acres)'
            elmId='lot_size'
            name='lot_size'
            maskOptions={{ allowDecimal: true, allowLeadingZeroes: true }}
          />
        </Grid.Column>
        <Grid.Column
          computer={8}
          tablet={8}
          mobile={16}
          className={cx('fields-row')}
        >
          <NumberInputField
            label='Square Feet'
            elmId='home_square_footage'
            name='home_square_footage'
            maskOptions={{ allowDecimal: true, allowLeadingZeroes: true }}
          />
        </Grid.Column>
        <Grid.Column
          computer={8}
          tablet={8}
          mobile={16}
          className={cx('fields-row')}
        >
          <DropdownField
            label='Property Type'
            elmId='structure_type_code'
            name='structure_type_code'
            option={[]}
          />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

export const PropertyBox = ({ property }: PropertyBoxProps) => {
  const isMobile = useIsMobileHook()
  const [isEditable, setIsEditable] = useState(false)
  return (
    <div className={cx('property-box-container')}>
      <CardContainer
        padding='small'
        borderRadius='medium'
        borderRadiusCorners='all'
        dropShadow='medium'
        className={cx('property-box')}
      >
        <Text.Header level={4}>
          <Icon
            name='map pin'
            className={cx('icons')}
            elmId='property_address_label'
          />
          Property Address
        </Text.Header>
        <AddressBox
          address={property.address}
          showIsConfirmed={property.is_confirmed && !isEditable}
        />
        <Text.Header
          level={4}
          className={cx('u-mb-2')}
          elmId='property_details_label'
        >
          <Icon name='home' className={cx('icons')} />
          Property Details
        </Text.Header>
        {isEditable ? <PropertyForm /> : <PropertyFields property={property} />}
        <Grid>
          <Grid.Row>
            <Grid.Column width={8}>
              <Button
                isStretch
                role='secondary'
                onClick={() => setIsEditable(!isEditable)}
                elmId='property_box_secondary_btn'
              >
                {isEditable ? 'Cancel' : 'Edit Details'}
              </Button>
            </Grid.Column>
            <Grid.Column width={8}>
              <Button isStretch role='primary' elmId='property_box_confirm_btn'>
                {isMobile ? 'Confirm' : 'Confirm and Continue'}
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </CardContainer>
    </div>
  )
}
